import { ExternalE2EEKeyProvider, Room, type RoomOptions } from 'livekit-client';

/**
 * Low-level building block only. Callers must authenticate and distribute the
 * key to every participant and enforce the room policy before using this.
 * This module is deliberately not wired into conference joins yet.
 */
export async function createEncryptedRoom(
  key: Uint8Array,
  options: Omit<RoomOptions, 'encryption' | 'e2ee'> = {},
): Promise<{ room: Room; dispose: () => void }> {
  if (!(key instanceof Uint8Array) || key.byteLength !== 32) {
    throw new Error('Cheia conferinței criptate trebuie să conțină 32 de octeți.');
  }
  if (typeof Worker === 'undefined' || !globalThis.crypto?.subtle) {
    throw new Error('Criptarea conferinței nu este disponibilă pe acest dispozitiv.');
  }
  const worker = new Worker(new URL('livekit-client/e2ee-worker', import.meta.url));
  try {
    const keyProvider = new ExternalE2EEKeyProvider();
    const keyCopy = new Uint8Array(key);
    try {
      await keyProvider.setKey(keyCopy.buffer);
    } finally {
      keyCopy.fill(0);
    }
    const room = new Room({ ...options, encryption: { keyProvider, worker } });
    await room.setE2EEEnabled(true);
    if (!room.localParticipant.isE2EEEnabled) throw new Error('Criptarea locală nu a putut fi activată.');
    return { room, dispose: () => worker.terminate() };
  } catch (error) {
    worker.terminate();
    throw error;
  }
}
